import React from "react";
import { connect } from "react-redux";
import { AppState } from "../../store";
import { withRouter, Redirect } from "react-router-dom";
import { loadClients } from "../../store/clients/actions";
import {
  changeAccountId,
  loadIntegrations,
  reSaveIntegration,
  saveIntegration
} from "../../store/integrations/actions";
import { Select, MenuItem } from "@mui/material";
import { ClientsState } from "../../store/clients/types";
import { SystemState } from "../../store/system/types";
import { IntegrationState } from "../../store/integrations/types";

import "./style.css";

interface IntegrationInterfaceProps {
  loadClients: typeof loadClients;
  loadIntegrations: typeof loadIntegrations;
  saveIntegration: typeof saveIntegration;
  changeAccountId: typeof changeAccountId;
  reSaveIntegration: typeof reSaveIntegration;
  // NOTE: We do NOT require clearIntegrationMessage to exist; we guard its usage.
  clearIntegrationMessage?: any;
  clients: ClientsState;
  system: SystemState;
  integrations: IntegrationState;
  history: any;
}

type State = {
  isCompleting: boolean;
  localError: string;
  diagnostics: string;
  showDiagnostics: boolean;
};

class Integrations extends React.Component<IntegrationInterfaceProps, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      isCompleting: false,
      localError: "",
      diagnostics: "",
      showDiagnostics: false,
    };
  }

  private safeClearMessage(): void {
    // No optional chaining; guard manually
    var fn = (this.props as any).clearIntegrationMessage;
    if (fn && typeof fn === "function") {
      fn();
    }
  }

  componentDidMount(): void {
    this.props.loadIntegrations();

    var urlStr = window.location.href;
    var urlObj = new URL(urlStr);
    var params = urlObj.searchParams;

    var stateParam = params.get("state");
    var code = params.get("code");

    var diagParts: string[] = [];
    diagParts.push("time=" + new Date().toISOString());
    diagParts.push("href=" + window.location.href);
    diagParts.push("has_code=" + (code ? "yes" : "no"));
    diagParts.push("has_state=" + (stateParam ? "yes" : "no"));
    diagParts.push("path=" + window.location.pathname);

    this.setState({ diagnostics: diagParts.join("\n") });

    if (stateParam && code) {
      // Clear any prior message if the app supports it
      this.safeClearMessage();

      this.setState({ isCompleting: true, localError: "" });

      try {
        this.props.saveIntegration({ code: code, state: stateParam }, this.props.history);

        // Clean the URL so refresh doesn't retry the one-time code
        this.props.history.replace("/integration");
      } catch (e: any) {
        var msg = "Facebook connection failed while completing authorization.";
        if (e && e.message) msg = msg + " " + e.message;
        this.setState({ localError: msg, isCompleting: false });
      }
    }
  }

  componentDidUpdate(prevProps: IntegrationInterfaceProps): void {
    if (this.state.isCompleting) {
      var prevAny: any = prevProps.integrations as any;
      var curAny: any = this.props.integrations as any;

      var prevMsg = prevAny && prevAny.message ? String(prevAny.message) : "";
      var curMsg = curAny && curAny.message ? String(curAny.message) : "";

      var prevErr = prevAny && prevAny.error ? true : false;
      var curErr = curAny && curAny.error ? true : false;

      var prevCount = prevProps.integrations && prevProps.integrations.adAccountLists
        ? prevProps.integrations.adAccountLists.length
        : 0;

      var curCount = this.props.integrations && this.props.integrations.adAccountLists
        ? this.props.integrations.adAccountLists.length
        : 0;

      // Stop spinner if an error appeared OR accounts appeared OR message changed
      if (curErr || curCount > 0 || (curMsg && curMsg !== prevMsg)) {
        this.setState({ isCompleting: false });
      }
    }
  }

  private copyDiagnostics(): void {
    var text = this.state.diagnostics ? String(this.state.diagnostics) : "";
    navigator.clipboard.writeText(text);
  }

  private toggleDiagnostics(): void {
    this.setState({ showDiagnostics: !this.state.showDiagnostics });
  }

  private goRetry(): void {
    this.safeClearMessage();
    this.setState({ isCompleting: false, localError: "", showDiagnostics: false });
    this.props.history.push("/accounts");
  }

  render(): JSX.Element {
    var integrationsAny: any = this.props.integrations as any;

    var reduxIsError = integrationsAny && integrationsAny.error ? true : false;
    var reduxMsg = integrationsAny && integrationsAny.message ? String(integrationsAny.message) : "";
    var reduxFbCode = integrationsAny && integrationsAny.fb_error_code ? String(integrationsAny.fb_error_code) : "";

    var reduxReq =
      integrationsAny &&
      integrationsAny.fb_required_permissions &&
      Array.isArray(integrationsAny.fb_required_permissions)
        ? integrationsAny.fb_required_permissions
        : [];

    var reduxGranted =
      integrationsAny &&
      integrationsAny.fb_granted_permissions &&
      Array.isArray(integrationsAny.fb_granted_permissions)
        ? integrationsAny.fb_granted_permissions
        : [];

    var localError = this.state.localError ? String(this.state.localError) : "";
    var effectiveError = localError ? localError : reduxIsError ? reduxMsg : "";

    var isCompleting = this.state.isCompleting;
    var diagnostics = this.state.diagnostics ? String(this.state.diagnostics) : "";
    var showDiagnostics = this.state.showDiagnostics;

    var adListCount = this.props.integrations && this.props.integrations.adAccountLists
      ? this.props.integrations.adAccountLists.length
      : 0;

    // Never blank: show something if not completing, no accounts, and no error
    var showEmptyState = !isCompleting && !effectiveError && adListCount === 0;

    return (
      <>
        {this.props.integrations.redirectUser ? <Redirect to="/accounts" /> : null}

        {/* Callback status + error UI */}
        {(isCompleting || !!effectiveError) ? (
          <div className="form_details">
            <div className="form_title">
              <h1>Facebook Connection</h1>
            </div>

            {isCompleting ? (
              <div style={{ padding: 12 }}>
                <h3>Completing authorization…</h3>
                <p>Please keep this page open.</p>

                <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                  <input
                    className="form_details--button"
                    type="button"
                    value={showDiagnostics ? "Hide diagnostics" : "Show diagnostics"}
                    onClick={() => this.toggleDiagnostics()}
                  />
                  <input
                    className="form_details--button"
                    type="button"
                    value="Copy diagnostics"
                    onClick={() => this.copyDiagnostics()}
                    disabled={!diagnostics}
                  />
                </div>

                {showDiagnostics ? (
                  <div style={{ marginTop: 12 }}>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        background: "#111",
                        color: "#eee",
                        padding: 12,
                        borderRadius: 8,
                      }}
                    >
                      {diagnostics}
                    </pre>
                  </div>
                ) : null}
              </div>
            ) : null}

            {effectiveError ? (
              <div style={{ padding: 12 }}>
                <h3 style={{ color: "crimson" }}>
                  {reduxFbCode === "MISSING_FACEBOOK_PERMISSION" ? "Permissions not granted" : "Something went wrong"}
                </h3>

                <p style={{ whiteSpace: "pre-wrap" }}>{effectiveError}</p>

                {reduxFbCode === "MISSING_FACEBOOK_PERMISSION" ? (
                  <div style={{ marginTop: 10 }}>
                    <p>
                      <b>Required permissions:</b> {reduxReq.length ? reduxReq.join(", ") : "ads_management"}
                    </p>
                    <p>
                      <b>Granted permissions:</b> {reduxGranted.length ? reduxGranted.join(", ") : "(none returned)"}
                    </p>
                    <p>
                      Click <b>Retry</b>, then approve the requested permissions in the Facebook prompt.
                    </p>
                  </div>
                ) : (
                  <p>
                    Click <b>Retry</b> and start the connection again. If this continues, open diagnostics and send it to support.
                  </p>
                )}

                <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                  <input
                    className="form_details--button"
                    type="button"
                    value="Retry Facebook Connection"
                    onClick={() => this.goRetry()}
                  />
                  <input
                    className="form_details--button"
                    type="button"
                    value={showDiagnostics ? "Hide diagnostics" : "Show diagnostics"}
                    onClick={() => this.toggleDiagnostics()}
                  />
                  <input
                    className="form_details--button"
                    type="button"
                    value="Copy diagnostics"
                    onClick={() => this.copyDiagnostics()}
                    disabled={!diagnostics}
                  />
                </div>

                {showDiagnostics ? (
                  <div style={{ marginTop: 12 }}>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        background: "#111",
                        color: "#eee",
                        padding: 12,
                        borderRadius: 8,
                      }}
                    >
                      {diagnostics}
                    </pre>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Empty state (prevents blank page) */}
        {showEmptyState ? (
          <div className="form_details">
            <div className="form_title">
              <h1>Integrations</h1>
            </div>
            <div style={{ padding: 12 }}>
              <p>Facebook authorized, but required permissions were not granted or there are no audiences linked to your account. Please reconnect and approve the requested permissions or make sure you have audiences linked to your account.</p>
              <p>Click Retry.</p>

              <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                <input
                  className="form_details--button"
                  type="button"
                  value="Retry Facebook Connection"
                  onClick={() => this.goRetry()}
                />
                <input
                  className="form_details--button"
                  type="button"
                  value={showDiagnostics ? "Hide diagnostics" : "Show diagnostics"}
                  onClick={() => this.toggleDiagnostics()}
                />
                <input
                  className="form_details--button"
                  type="button"
                  value="Copy diagnostics"
                  onClick={() => this.copyDiagnostics()}
                  disabled={!diagnostics}
                />
              </div>

              {showDiagnostics ? (
                <div style={{ marginTop: 12 }}>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      background: "#111",
                      color: "#eee",
                      padding: 12,
                      borderRadius: 8,
                    }}
                  >
                    {diagnostics}
                  </pre>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Existing account picker UI */}
        {adListCount > 0 ? (
          <div className="form_details">
            <div className="form_title">
              <h1>Required Info</h1>
            </div>

            <div className="flexBox">
              <div id="w100">
                <label className="field_label">
                  <h3>Ad Account Selection</h3>
                </label>
                <div>
                  <Select
                    required
                    placeholder="Select Account"
                    value={this.props.integrations.selectedAccount}
                    onChange={(event) => this.props.changeAccountId((event.target as any).value)}
                  >
                    {this.props.integrations.adAccountLists.map((e: any, key: number) => (
                      <MenuItem key={key} value={e.account_id}>
                        {e.name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            <input
              className="form_details--button"
              type="button"
              value="Create My Audience"
              onClick={() => {
                var state_param = (this.props.integrations as any).facebookState;
                if (!state_param) {
                  this.setState({
                    localError: "Missing saved Facebook state. Please retry the Facebook connection.",
                    isCompleting: false,
                  });
                  return;
                }

                this.safeClearMessage();
                this.setState({ localError: "" });

                this.props.reSaveIntegration({
                  accountId: this.props.integrations.selectedAccount,
                  state: state_param,
                  integrationId: (this.props.integrations as any).integrationId || null,
                });
              }}
            />
          </div>
        ) : null}
      </>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  clients: state.clients,
  scripts: (state as any).scripts,
  system: state.system,
  integrations: state.integrations,
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    loadClients: () => dispatch(loadClients()),
    loadIntegrations: () => dispatch(loadIntegrations()),
    saveIntegration: (data?: any, history?: any) => dispatch(saveIntegration(data, history)),
    reSaveIntegration: (data?: any) => dispatch(reSaveIntegration(data)),
    changeAccountId: (data: any) => dispatch(changeAccountId(data)),

    // If you have clearIntegrationMessage exported, wire it here.
    // If not, leaving it undefined is safe (page will not crash).
    clearIntegrationMessage: () => {
      try {
        // Dynamically require avoids crashing if not exported in some bundlers,
        // but simplest is just to not wire it unless you confirm it's exported.
        dispatch({ type: "CLEAR_MESSAGE", payload: null });
      } catch (e) {
        // ignore
      }
    },
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Integrations));
